// Colonne contextuelle du pied de page — lot 16.
//
// Le pied de page a trois colonnes fixes, identiques sur toutes les pages, et
// une colonne qui suit le sujet de la page : les communes sur une page
// commune, les pages garde-meubles sur une page garde-meubles, les pays sur
// une page internationale, les fiches opérateurs sur une page du hub
// changement d'adresse. Un pied de page identique sur cent pages est traité
// comme de la navigation, et ses liens sont pondérés en conséquence ; un bloc
// qui suit le sujet de la page consolide le silo qu'elle construit.
//
// Ce module est PUR : il ne lit ni le DOM ni l'URL du navigateur. Il reçoit la
// section de la page, sa route, et les URL que son corps lie déjà, et rend une
// liste de liens. Le pied de page l'appelle au pré-rendu comme à l'hydratation,
// avec les mêmes données — le HTML livré et le HTML monté sont donc les mêmes.
//
// Règles, dans l'ordre où elles s'appliquent :
//   1. les listes sont RÉSOLUES depuis les données publiées de la section
//      (verrou `estPubliable`), jamais écrites en dur ;
//   2. la page courante n'est jamais liée depuis son propre pied de page ;
//   3. une URL déjà liée dans le corps de la page est retirée, et la liste
//      est complétée par les entrées suivantes — un lien deux fois sur la
//      même page n'apporte rien ;
//   4. neuf liens au plus ;
//   5. si la section fournit moins de trois liens, repli sur « generique » ;
//   6. jamais de colonne vide : si même « generique » tombe sous trois liens
//      après exclusion du corps (la page des zones lie toutes les communes),
//      les exclusions du corps sont levées — la page courante reste exclue.
import {
  RACINE_ZONES, communeUrl, communesDuMenu, getCommuneBySlug, getNeighborCommunes, getPublishedCommunes,
  type CommuneSEO,
} from './communes';
import { estPubliable, PAGE_CHANGEMENT_ADRESSE, PAGE_CONGE_DEMENAGEMENT } from './pages-en-preparation';
import {
  destinationsOrdonnees, cheminDestination, RACINE_INTERNATIONAL,
  supportsPublies, cheminSupport, libelleSupport,
} from './destinations';
import { PAGES_USAGES, cheminUsage } from './garde-meubles-usages';
import { PAGES_GARDE_MEUBLES, cheminGardeMeublesCommune, communeDe } from './garde-meubles-communes';
import { operateursAvecFiche, cheminOperateur } from './changement-adresse';
import { SECTION_PAR_DEFAUT, type Section } from './navigation';

export interface LienPied {
  label: string;
  to: string;
}

/**
 * Ce que la page sait d'elle-même et que la colonne peut exploiter. Déclaré par
 * le gabarit, jamais déduit de l'URL : une page commune passe sa commune.
 */
export interface ContexteColonne {
  commune?: CommuneSEO;
}

/** Un intitulé et ses liens : la colonne en rend un, ou deux quand elle complète. */
export interface GroupeLiens {
  titre: string;
  liens: LienPied[];
}

export interface ColonneContextuelle {
  /** Section réellement rendue — « generique » après repli. */
  section: Section;
  /** Intitulé du premier groupe. */
  titre: string;
  /** Tous les liens de la colonne, dans l'ordre de rendu. */
  liens: LienPied[];
  /** Les groupes rendus : un seul d'ordinaire, deux quand la section est complétée. */
  groupes: GroupeLiens[];
  /**
   * Ce qui a joué : `aucun` ; `complete` (la section fournissait entre un et
   * deux liens : ils sont gardés sous leur intitulé, et la colonne est
   * complétée par les communes sous le leur) ; `generique` (la section ne
   * fournissait rien) ; `corps` (dernier recours : les exclusions du corps ont
   * été levées pour ne pas livrer une colonne vide).
   */
  repli: 'aucun' | 'complete' | 'generique' | 'corps';
}

/** Neuf liens au plus : le pied de page reste sous vingt-six liens internes. */
export const PLAFOND = 9;
/** En dessous, la colonne ne dit rien : repli sur « generique ». */
export const MINIMUM = 3;

export const TITRES: Record<Section, string> = {
  demenagement: 'Déménagement dans la province',
  generique: 'Déménagement dans la province',
  'garde-meubles': 'Garde-meubles',
  international: "Déménager à l'étranger",
  adresse: "Changement d'adresse",
};

/** Hubs de section, liés depuis leur propre colonne (hors page courante). */
export const HUB_GARDE_MEUBLES_LIEGE = '/garde-meubles/garde-meubles-liege';
export const PAGE_PRIX_GARDE_MEUBLES = '/garde-meubles/prix-garde-meubles-liege';
export const HUB_INTERNATIONAL = RACINE_INTERNATIONAL;
export const LIEN_TOUTES_COMMUNES: LienPied = { label: 'Toutes les communes de la province', to: RACINE_ZONES };

const trierParLabel = (a: LienPied, b: LienPied) => a.label.localeCompare(b.label, 'fr');

/**
 * Deuxième couronne d'une commune : les voisines publiées de ses voisines
 * publiées, hors elle-même et hors ses voisines directes — que le corps de la
 * page lie déjà. Classées par nombre de voisines partagées, puis par nom.
 *
 * C'est le cluster géographique de la page, calculé depuis `communesVoisines`,
 * relevé et validé en base. Avant, les soixante-dix pages communes rendaient
 * la même colonne — les huit communes du menu — soit une navigation sitewide
 * de plus : sept communes recevaient quatre-vingt-dix liens chacune et
 * quarante-deux n'en recevaient aucun. Avec la deuxième couronne, chaque
 * commune publiée en reçoit entre deux et quatorze, et le silo « des pages
 * communes qui se lient entre elles » existe réellement.
 */
export function deuxiemeCouronne(commune: CommuneSEO): CommuneSEO[] {
  const directes = getNeighborCommunes(commune);
  const exclues = new Set([commune.id, ...directes.map((v) => v.id)]);
  const partagees = new Map<string, number>();
  for (const v of directes) {
    for (const w of getNeighborCommunes(v)) {
      if (!exclues.has(w.id)) partagees.set(w.id, (partagees.get(w.id) ?? 0) + 1);
    }
  }
  return [...partagees.entries()]
    .map(([id, n]) => ({ c: getCommuneBySlug(id)!, n }))
    .sort((a, b) => b.n - a.n || a.c.nom.localeCompare(b.c.nom, 'fr'))
    .map(({ c }) => c);
}

/**
 * Communes hors menu, une par arrondissement à tour de rôle, les plus proches
 * du dépôt d'abord dans chaque arrondissement.
 *
 * Sert de complément sur les pages dont le corps lie déjà les communes du
 * menu — l'accueil, les hubs. Le complément par seule distance au dépôt
 * remplissait ces colonnes de neuf communes du même arrondissement ; celui-ci
 * couvre la province, ce que le titre de la colonne promet.
 */
export function communesParArrondissement(exclues: Set<string>): CommuneSEO[] {
  const files = new Map<string, CommuneSEO[]>();
  for (const c of getPublishedCommunes()) {
    if (exclues.has(c.id)) continue;
    const arr = c.arrondissement ?? '';
    if (!files.has(arr)) files.set(arr, []);
    files.get(arr)!.push(c);
  }
  for (const file of files.values()) {
    file.sort((a, b) => (a.distanceDepotKm ?? Infinity) - (b.distanceDepotKm ?? Infinity) || a.nom.localeCompare(b.nom, 'fr'));
  }
  const ordre = [...files.keys()].sort((a, b) => a.localeCompare(b, 'fr'));
  const out: CommuneSEO[] = [];
  for (let i = 0; files.size > 0; i++) {
    for (const arr of ordre) {
      const c = files.get(arr)?.[i];
      if (c) out.push(c);
    }
    if (ordre.every((arr) => (files.get(arr)?.length ?? 0) <= i + 1)) break;
  }
  return out;
}

/**
 * Communes de la colonne « Déménagement dans la province », dans l'ordre :
 * la deuxième couronne de la page si c'est une page commune, puis les
 * communes du menu (`featured_nav`, par `nav_order`), puis le complément par
 * arrondissement. Le résolveur retire ensuite ce que le corps lie déjà et
 * plafonne.
 */
function candidatsCommunes(contexte: ContexteColonne): LienPied[] {
  const couronne = contexte.commune ? deuxiemeCouronne(contexte.commune) : [];
  const menu = communesDuMenu();
  const vues = new Set([...couronne, ...menu].map((c) => c.id));
  if (contexte.commune) vues.add(contexte.commune.id);
  const complement = communesParArrondissement(vues);
  const ordonnees: CommuneSEO[] = [];
  const dejaVues = new Set<string>();
  for (const c of [...couronne, ...menu, ...complement]) {
    if (dejaVues.has(c.id)) continue;
    dejaVues.add(c.id);
    ordonnees.push(c);
  }
  return ordonnees.map((c) => ({ label: `Déménagement à ${c.nom}`, to: communeUrl(c) }));
}

function candidatsGardeMeubles(): LienPied[] {
  const usages = PAGES_USAGES
    .map((p) => ({ label: p.h1.split(':')[0].trim(), to: cheminUsage(p.slug) }))
    .sort(trierParLabel);
  const communes = PAGES_GARDE_MEUBLES
    .map((p) => ({ label: `Garde-meubles pour ${communeDe(p)?.nom ?? p.slug}`, to: cheminGardeMeublesCommune(p.slug) }))
    .sort(trierParLabel);
  return [
    { label: 'Garde-meubles à Liège', to: HUB_GARDE_MEUBLES_LIEGE },
    { label: "Prix d'un garde-meubles", to: PAGE_PRIX_GARDE_MEUBLES },
    ...usages,
    ...communes,
  ];
}

/**
 * Le pilier, puis une entrée par page pays (lot 17), dans l'ordre `nav_order`
 * de la table `destinations` — France, Luxembourg, Pays-Bas, Allemagne,
 * Suisse, Espagne, Italie, Portugal. Neuf liens au plus, sous le plafond.
 * Le verrou `estPubliable` retire ensuite les pays non publiés : la colonne
 * grandit seule à chaque vague, sans retouche du composant.
 */
function candidatsInternational(): LienPied[] {
  return [
    { label: 'Déménagement international', to: HUB_INTERNATIONAL },
    ...destinationsOrdonnees().map((d) => ({ label: `Déménagement Belgique – ${d.paysNom}`, to: cheminDestination(d.slug) })),
    // Les trois pages supports — prix, douane, installation — étaient absentes
    // de leur propre section : la colonne ne proposait que le hub et les huit
    // pays. Mesuré sur le HTML livré avant ce lot, `s-installer-en-belgique`
    // ne recevait que 9 liens, tous depuis le corps des pages pays ; aucune
    // page ne la liait depuis son pied de page.
    //
    // Placées APRÈS les pays, donc servies en dernier sous le plafond de 9.
    // C'est voulu : sur une page pays, le corps lie déjà les trois supports,
    // `retenir()` les écarte à ce titre, et la colonne se remplit des pays
    // voisins — ce qu'on veut. Sur le hub et sur les supports eux-mêmes, où
    // le corps ne les porte pas, elles entrent.
    ...supportsPublies().map((s) => ({ label: libelleSupport(s.slug), to: cheminSupport(s.slug) })),
  ];
}

/** CILE et RESA en tête — les deux démarches que tout ménage liégeois fait — puis l'alphabet. */
const OPERATEURS_EN_TETE = ['cile', 'resa'];

function candidatsAdresse(): LienPied[] {
  const rang = (slug: string) => {
    const i = OPERATEURS_EN_TETE.indexOf(slug);
    return i === -1 ? OPERATEURS_EN_TETE.length : i;
  };
  const fiches = operateursAvecFiche()
    .slice()
    .sort((a, b) => rang(a.slug) - rang(b.slug) || a.nom.localeCompare(b.nom, 'fr'))
    .map((o) => ({ label: `Changement d'adresse ${o.nom}`, to: cheminOperateur(o.slug) }));
  return [
    { label: "Changement d'adresse à Liège", to: PAGE_CHANGEMENT_ADRESSE },
    ...fiches,
    { label: 'Congé de déménagement', to: PAGE_CONGE_DEMENAGEMENT },
  ];
}

/**
 * Tous les candidats d'une section, dans l'ordre, AVANT exclusions et plafond.
 * Le verrou de publication s'applique ici, une fois pour toutes les sections :
 * une page verrouillée n'existe pas pour le pied de page.
 */
export function candidatsDeSection(section: Section, contexte: ContexteColonne = {}): LienPied[] {
  const bruts = section === 'garde-meubles' ? candidatsGardeMeubles()
    : section === 'international' ? candidatsInternational()
    : section === 'adresse' ? candidatsAdresse()
    : [...candidatsCommunes(contexte), LIEN_TOUTES_COMMUNES];
  return bruts.filter((l) => estPubliable(l.to));
}

/** Une URL interne ramenée à son chemin : sans domaine, sans ancre, sans slash final. */
export function normaliserChemin(url: string): string {
  let c = url.replace(/^https?:\/\/[^/]+/, '').replace(/[#?].*$/, '');
  if (c.length > 1 && c.endsWith('/')) c = c.slice(0, -1);
  return c || '/';
}

function retenir(section: Section, exclus: Set<string>, contexte: ContexteColonne, plafond = PLAFOND): LienPied[] {
  const liens = candidatsDeSection(section, contexte).filter((l) => !exclus.has(normaliserChemin(l.to)));
  if (liens.length <= plafond) return liens;
  // Colonne géographique : « Toutes les communes » reste la dernière entrée,
  // quel que soit le nombre de communes disponibles avant elle.
  const toutes = liens.find((l) => l.to === LIEN_TOUTES_COMMUNES.to);
  return toutes ? [...liens.filter((l) => l !== toutes).slice(0, plafond - 1), toutes] : liens.slice(0, plafond);
}

export function colonneContextuelle(
  section: Section,
  routeCourante: string,
  exclure: Iterable<string> = [],
  contexte: ContexteColonne = {},
): ColonneContextuelle {
  const courante = normaliserChemin(routeCourante);
  const exclus = new Set([courante, ...Array.from(exclure, normaliserChemin)]);

  let effective: Section = section;
  let repli: ColonneContextuelle['repli'] = 'aucun';
  let liens = retenir(effective, exclus, contexte);

  if (liens.length > 0 && liens.length < MINIMUM && effective !== SECTION_PAR_DEFAUT) {
    // La section existe mais n'a pas encore assez de pages publiées — le
    // garde-meubles avant les lots 7 et 8, l'international avant le lot 10.
    // Remplacer la colonne entière effaçait le seul signal de silo disponible ;
    // on garde les liens de la section sous leur intitulé et on complète, sous
    // le sien, avec la colonne générique. Le silo grandit seul à chaque
    // publication, jusqu'à se suffire.
    const dejaRendus = new Set([...exclus, ...liens.map((l) => normaliserChemin(l.to))]);
    const complement = retenir(SECTION_PAR_DEFAUT, dejaRendus, contexte, PLAFOND - liens.length);
    if (liens.length + complement.length >= MINIMUM) {
      const groupes = [
        { titre: TITRES[effective], liens },
        { titre: TITRES[SECTION_PAR_DEFAUT], liens: complement },
      ];
      return { section: effective, titre: TITRES[effective], liens: [...liens, ...complement], groupes, repli: 'complete' };
    }
  }

  if (liens.length < MINIMUM && effective !== SECTION_PAR_DEFAUT) {
    effective = SECTION_PAR_DEFAUT;
    repli = 'generique';
    liens = retenir(effective, exclus, contexte);
  }
  if (liens.length < MINIMUM) {
    // Dernier recours : le corps de la page lie déjà tout ce que la colonne
    // aurait proposé. Mieux vaut répéter un lien que livrer une colonne vide.
    effective = SECTION_PAR_DEFAUT;
    repli = 'corps';
    liens = retenir(effective, new Set([courante]), contexte);
  }
  const titre = TITRES[effective];
  return { section: effective, titre, liens, groupes: [{ titre, liens }], repli };
}
